import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  UtensilsCrossed,
  ClipboardList,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChefHat,
  Users,
  Wallet,
  ShoppingCart,
  QrCode,
  Gift,
  Beef,
  Store,
  Shield,
  Monitor,
  Grid,
  MapPin,
  History,
  Truck,
  Megaphone,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Footer } from "@/components/Footer";

import { configService } from "@/services/ConfigService";
import Logo from "@/components/ui/Logo";
import { useHostContext } from "@/shell/useHostContext";

// Define which routes each role can access
const ROLE_ACCESS = {
  admin: ["all"],
  support: ["all"], // Soporte tiene acceso total
  cashier: ["/admin/pos", "/admin/orders", "/admin/cash", "/admin/tables", "/admin/delivery"],
  kitchen: ["/admin/kitchen"],
  driver: ["/driver"],
};

export const AdminLayout = ({ children, title, noPadding = false, noFooter = false }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [config, setConfig] = useState(configService.getConfig());
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { hubPrefix } = useHostContext();

  useEffect(() => {
    const loadConfig = async () => {
      const data = await configService.loadConfig();
      setConfig(data);
      
      // Inject Branding Colors
      const primaryColor = data?.primary_color || data?.branding?.primary_color;
      const secondaryColor = data?.secondary_color || data?.branding?.secondary_color;

      if (primaryColor) {
        document.documentElement.style.setProperty('--primary', primaryColor);
      } else {
        document.documentElement.style.setProperty('--primary', '#1a40d0'); // default deep blue
      }
      
      if (secondaryColor) {
        document.documentElement.style.setProperty('--secondary', secondaryColor);
      }
    };
    loadConfig();
  }, []);

  // All navigation items
  const allNavItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Dashboard", roles: ["admin", "support"] },
    { path: "/admin/branches", icon: MapPin, label: "Sucursales", roles: ["admin", "support"] },
    { path: "/admin/delivery", icon: Truck, label: "Delivery Hub", roles: ["admin", "support", "cashier"] },
    { path: "/admin/pos", icon: ShoppingCart, label: "TPV / Caja", roles: ["admin", "cashier", "support"] },
    { path: "/admin/tables", icon: Grid, label: "Mapa de Mesas", roles: ["admin", "cashier", "support"] },
    { path: "/admin/kitchen", icon: ChefHat, label: "Cocina", roles: ["admin", "kitchen", "support"] },
    { path: "/admin/orders", icon: ClipboardList, label: "Pedidos", roles: ["admin", "cashier", "support"] },
    { path: "/admin/orders?view=history", icon: History, label: "Historial", roles: ["admin", "cashier", "support"] },
    { path: "/admin/cash", icon: Wallet, label: "Reporte Caja", roles: ["admin", "cashier", "support"] },
    { path: "/admin/customers", icon: Users, label: "Clientes", roles: ["admin", "support"] },
    { path: "/admin/marketing", icon: Megaphone, label: "Marketing", roles: ["admin", "support"] },
    { path: "/admin/offers", icon: Gift, label: "Ofertas", roles: ["admin", "support"] },
    { path: "/admin/qr-mesas", icon: QrCode, label: "QR Mesas", roles: ["admin", "support"] },
    { path: "/admin/qr-display", icon: Monitor, label: "QR Pantalla Pedidos", roles: ["admin", "support"] },
    { path: "/admin/menu", icon: UtensilsCrossed, label: "Menú", roles: ["admin", "support"] },
    { path: "/admin/custom-taco", icon: Grid, label: "Personalizador", roles: ["admin", "support"] },
    { path: "/admin/integrations", icon: Store, label: "Integraciones", roles: ["admin", "support"] },
    { path: "/admin/verifactu", icon: Shield, label: "VeriFactu", roles: ["admin", "support"] },
    { path: "/admin/users", icon: Users, label: "Personal", roles: ["admin", "support"] },
    { path: "/admin/settings", icon: Settings, label: "Configuración", roles: ["admin", "support"] },
  ];

  // Filter nav items based on user role and configuration
  const isDeliveryOnly = config?.delivery?.delivery_only_mode === true;
  const isDemo = sessionStorage.getItem("demo_mode") === "true";

  const navItems = allNavItems.filter(item => {
    // Demo restrictions: Permitimos TPV completo (Caja, Mesas, Reporte, Pedidos)
    if (isDemo) {
      const allowedPaths = ["/admin/pos", "/admin/tables", "/admin/cash", "/admin/orders", "/admin/delivery"];
      return allowedPaths.some(p => item.path.startsWith(p));
    }

    // Role check
    if (!item.roles.includes(user?.role)) return false;

    // Delivery only mode check
    if (isDeliveryOnly) {
      const posRelatedPaths = ["/admin/pos", "/admin/tables", "/admin/qr-mesas", "/admin/qr-display"];
      if (posRelatedPaths.includes(item.path)) return false;
    }

    // Feature flags check
    const features = config?.features || {};
    if (item.path === "/admin/custom-taco" && !features.custom_taco) return false;
    if (item.path === "/admin/integrations" && !features.integrations) return false;
    if (item.path === "/admin/verifactu" && !features.verifactu) return false;

    return true;
  });

  // Check if user has access to current route
  useEffect(() => {
    const userRole = user?.role;
    if (userRole && userRole !== "admin") {
      const allowedPaths = ROLE_ACCESS[userRole] || [];
      const hasAccess = allowedPaths.includes("all") ||
        allowedPaths.some(path => location.pathname.startsWith(path));

      if (!hasAccess) {
        // Redirect to their allowed page (modified to prevent infinite loop on /admin/pos)
        if (userRole === "cashier") {
          if (!location.pathname.startsWith("/admin/pos") && !location.pathname.startsWith("/admin/orders")) {
            navigate("/admin/pos", { replace: true });
          }
        } else if (userRole === "kitchen") {
          navigate("/admin/kitchen", { replace: true });
        }
      }
    }
  }, [location.pathname, user?.role, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-black border-b border-gray-800 z-40 flex items-center px-4 justify-between">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            className="text-primary"
            data-testid="admin-menu-toggle"
          >
            <Menu className="w-6 h-6" />
          </Button>
          <h1 className="ml-4 font-heading font-bold text-lg text-primary" style={{ color: 'var(--primary)' }}>{title}</h1>
        </div>
        {/* Display Button - Mobile - Hidden in POS, Orders, Cash Report, Kitchen, and Demo */}
        {!isDemo && !["/admin/pos", "/admin/orders", "/admin/cash", "/admin/kitchen"].includes(location.pathname) && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => window.open("/pedidos-local", "_blank", "width=1920,height=1080")}
            className="text-green-400 hover:text-green-300"
            title="Abrir Pantalla de Pedidos"
          >
            <Monitor className="w-5 h-5" />
          </Button>
        )}
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/70 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 h-full w-64 bg-black border-r border-gray-800 z-50 transition-transform duration-300 lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          {/* Logo */}
          <div className="h-20 flex items-center justify-between px-6 border-b border-gray-800 shrink-0">
            <Link to="/admin" className="flex items-center gap-3">
              {config?.logo || config?.branding?.logo_url ? (
                <img 
                  src={config.logo || config.branding?.logo_url} 
                  alt={config.name} 
                  className="w-8 h-8 rounded-lg object-contain bg-white/10 p-1"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <Logo size={14} variant="icon" />
              )}
              <div className="flex flex-col">
                <span className="font-bold text-white text-xs tracking-tight uppercase">
                  {(config?.name || config?.restaurant_name || "Mi Restaurante").split(' ')[0]} <span style={{ color: config?.primary_color || config?.branding?.primary_color || 'var(--primary)' }}>{(config?.name || config?.restaurant_name || "").split(' ').slice(1).join(' ') || "POS"}</span>
                </span>
                <span className="text-[10px] text-gray-500 font-medium">
                  {isDeliveryOnly ? 'DELIVERY HUB' :
                    user?.role === 'cashier' ? 'Cajero' :
                      user?.role === 'kitchen' ? 'Cocina' :
                        user?.role === 'driver' ? 'Repartidor' : 'Panel Admin'}
                </span>
                {isDeliveryOnly && (
                  <span className="text-[8px] bg-primary/20 text-primary px-1.5 py-0.5 rounded-full mt-0.5 font-black self-start">MODO LOGÍSTICA</span>
                )}
              </div>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-gray-400"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation with Scroll */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1 scrollbar-thin scrollbar-thumb-gray-800 scrollbar-track-transparent">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`admin-nav-${item.label.toLowerCase()}`}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors",
                    isActive
                      ? "bg-primary text-black shadow-[0_0_15px_rgba(var(--primary-rgb,234,179,8),0.3)]"
                      : "text-gray-400 hover:bg-gray-900 hover:text-primary"
                  )}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}

            {/* Display Button - Hidden in POS, Orders, Cash Report, Kitchen */}
            {!isDemo && !["/admin/pos", "/admin/orders", "/admin/cash", "/admin/kitchen"].includes(location.pathname) && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-green-500 text-green-400 hover:bg-green-500 hover:text-black transition-colors"
                  onClick={() => window.open("/pedidos-local", "_blank", "width=1920,height=1080")}
                  data-testid="open-display-btn"
                >
                  <Monitor className="w-4 h-4 mr-2" />
                  🖥️ Pantalla de Pedidos
                </Button>
              </div>
            )}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-gray-800">

            <div className="flex items-center gap-3 mb-4 group/user">
              <div className="relative">
                <img
                  src={user?.picture || config.logo || "/icons/logo.png"}
                  alt={user?.name}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(config?.name || "Admin")}&background=000&color=fff`;
                  }}
                  className="w-10 h-10 rounded-full bg-black object-cover border border-gray-700 group-hover/user:border-amber-500/50 transition-colors"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-white truncate text-sm">{user?.name || "Admin"}</p>
                <p className="text-[10px] text-gray-500 truncate uppercase tracking-tight">{user?.email}</p>
              </div>
            </div>

            <div className="flex gap-2">
              {!isDemo && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 border-gray-700 text-gray-300 hover:text-primary hover:border-primary"
                  onClick={() => navigate("/")}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Tienda
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-red-500"
                onClick={handleLogout}
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen bg-gray-950 flex flex-col relative">
        <div className={cn("flex-1", noPadding ? "p-0" : "p-4 lg:p-8")}>{children}</div>
        {!noFooter && (
          <div className="lg:block">
            <Footer />
          </div>
        )}
        {/* Floating Display Button - Desktop only, Hidden in POS/Orders/Cash/Kitchen */}
        {!isDemo && !["/admin/pos", "/admin/orders", "/admin/cash", "/admin/kitchen"].includes(location.pathname) && (
          <Button
            onClick={() => window.open(`${hubPrefix || ""}/pedidos-local`, "_blank", "width=1920,height=1080")}
            className="hidden lg:flex fixed bottom-6 right-6 h-14 px-6 bg-green-500 hover:bg-green-600 text-black font-bold shadow-2xl z-50 items-center gap-2"
            title="Abrir Pantalla de Pedidos"
          >
            <Monitor className="w-5 h-5" />
            🖥️ Pantalla de Pedidos
          </Button>
        )}
      </main>
    </div>
  );
};

export default AdminLayout;
